import React from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Image from 'components/AppImage';

const StaffDetailsModal = ({ isOpen, onClose, staff, onEdit }) => {
  if (!isOpen || !staff) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success text-success-foreground', label: 'Active', icon: 'CheckCircle' },
      inactive: { color: 'bg-muted text-muted-foreground', label: 'Inactive', icon: 'XCircle' },
      suspended: { color: 'bg-warning text-warning-foreground', label: 'Suspended', icon: 'AlertCircle' }
    };
    
    const config = status? statusConfig?.active : statusConfig?.inactive;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={16} className="mr-1" />
        {config?.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    let roleDescription = 'N/A';
    const roleColors = {
      'Full-Access': 'bg-primary text-primary-foreground',
      'Supervisor': 'bg-accent text-accent-foreground',
      'Washer': 'bg-secondary text-secondary-foreground',
      'Cashier': 'bg-muted text-muted-foreground'
    };


    if(role.admin && role.owner && role.reporting && role.releasing && role.scanning){
      roleDescription = 'Full-Access';
    }
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${roleColors[roleDescription] || 'bg-muted text-muted-foreground'}`}>
        {roleDescription}
      </span>
    );
  };

  const permissionLabels = {
    'owner': 'Business owner',
    'admin': 'Full admin access',
    'reporting': 'Viewing dashboards & reports',
    'scanning': 'Scanning of vehicle bookings',
    'releasing': 'Releasing completed vehicles',
  };

  const InfoRow = ({ icon, label, value, className = '' }) => (
    <div className={`flex items-start space-x-3 ${className}`}>
      <Icon name={icon} size={18} className="text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-muted-foreground">{label}</div>
        <div className="text-foreground break-words">{value || 'Not provided'}</div>
      </div>
    </div>
  );

  const getStaffPermissions = ()=>{
    const staffPermissions = [];

    if(staff?.owner) staffPermissions.push('owner');
    if(staff?.admin) staffPermissions.push('admin');
    if(staff?.reporting) staffPermissions.push('reporting');
    if(staff?.scanning) staffPermissions.push('scanning');
    if(staff?.releasing) staffPermissions.push('releasing');

    return(
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground flex items-center">
          <Icon name="Shield" size={20} className="mr-2" />
          Permissions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {staffPermissions?.map((permission) => (
            <div key={permission} className="flex items-center space-x-2 p-2 bg-muted/50 rounded-md">
              <Icon name="Check" size={14} className="text-success flex-shrink-0" />
              <span className="text-sm text-foreground">
                {permissionLabels?.[permission] || permission}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-1002 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-card border border-border rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
            <div className="flex items-center space-x-4">
              {/* <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex-shrink-0">
                <Image
                  src={staff?.avatar}
                  alt={staff?.name}
                  className="w-full h-full object-cover"
                />
              </div> */}
              <div>
                <h2 className="text-xl font-semibold text-foreground">{staff?.name} {staff?.surname}</h2>
                <div className="flex items-center space-x-3 mt-1">
                  {getRoleBadge(staff)}
                  {getStatusBadge(staff?.active)}
                </div>
                {/* <p className="text-sm text-muted-foreground mt-1">ID: {staff?.employeeId}</p> */}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => onEdit(staff)}>
                <Icon name="Edit" size={16} className="mr-2" />
                Edit
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1">
            <div className="p-6 space-y-8">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="Contact" size={20} className="mr-2" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoRow icon="Mail" label="Email Address" value={staff?.email} />
                  <InfoRow icon="Phone" label="Phone Number" value={staff?.contactNo} />
                  <InfoRow icon="MapPin" label="Address" value={staff?.address} className="md:col-span-2" />
                </div>
              </div>

              {/* Emergency Contact */}
              {/* <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="AlertTriangle" size={20} className="mr-2" />
                  Emergency Contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoRow icon="User" label="Contact Name" value={staff?.emergencyContact} />
                  <InfoRow icon="Phone" label="Contact Phone" value={staff?.emergencyPhone} />
                </div>
              </div> */}

              {/* Employment Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="Briefcase" size={20} className="mr-2" />
                  Employment Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoRow 
                    icon="Calendar" 
                    label="Hire Date" 
                    value={staff?.created ? new Date(staff.created)?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'Not provided'} 
                  />
                  {/* <InfoRow 
                    icon="DollarSign" 
                    label="Annual Salary" 
                    value={staff?.salary ? `$${parseInt(staff?.salary)?.toLocaleString()}` : 'Not specified'} 
                  /> */}
                </div>
              </div>

              {/* Permissions */}
              {(staff?.admin || staff?.owner || staff?.releasing || staff.scanner || staff.reporting) && (
                getStaffPermissions()
              )}

              {/* Notes */}
              {staff?.notes && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground flex items-center">
                    <Icon name="FileText" size={20} className="mr-2" />
                    Notes
                  </h3>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <p className="text-foreground whitespace-pre-wrap">{staff?.notes}</p>
                  </div>
                </div>
              )}

              {/* Activity Timeline */}
              {/* <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="Clock" size={20} className="mr-2" />
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                    <div className="w-2 h-2 bg-success rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground">Profile Updated</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date()?.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground">Account Created</div>
                      <div className="text-xs text-muted-foreground">
                        {staff?.hireDate ? new Date(staff.hireDate)?.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : 'Date not available'}
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30 flex-shrink-0">
            <div className="text-sm text-muted-foreground">
              Last updated: {new Date(staff.updated)?.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button onClick={() => onEdit(staff)} iconName="Edit" iconPosition="left" iconSize={16}>
                Edit Staff
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDetailsModal;